import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

import { CartProvider } from "./context/CartContext";
import { AdminProvider } from "./context/AdminContext";
import { useAdmin } from "./context/AdminContext";
import { ProductProvider } from "./context/ProductContext";

import NavBar from "./components/NavBar";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import ScrollToTop from "./components/ScrollToTop";

// Home is the landing page almost every visit starts on, so it's imported
// eagerly. Everything else is lazy-loaded so the first paint only ships the
// JS a customer actually needs for that visit.
import Home from "./pages/Home";
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const Unsubscribe = lazy(() => import("./Unsubscribe"));

// Admin pages are only ever visited by staff, never by customers — lazy-load
// them (and everything they pull in, e.g. AdminProductForm's image-upload
// utilities) so that code stays out of the bundle everyone else downloads.
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));

// Admin route: show login if not authenticated
const AdminRoute = () => {
  const { isAdmin } = useAdmin();
  return (
    <Suspense fallback={null}>
      {isAdmin ? <AdminDashboard /> : <AdminLogin />}
    </Suspense>
  );
};

// Main layout wrapper
const AppLayout = () => {
  const location = useLocation();

  const isAdmin = location.pathname.startsWith("/admin");
  const isUnsubscribe = location.pathname === "/unsubscribe";
  const isCheckout = location.pathname === "/checkout";
  const isSuccess = location.pathname === "/order-success";

  const minimal = isAdmin || isUnsubscribe;

  return (
    <>
      <ScrollToTop />
      {!minimal && <NavBar />}

      <CartDrawer />

      <main>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order-success" element={<OrderSuccess />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/admin" element={<AdminRoute />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>

      {!minimal && !isCheckout && !isSuccess && <Footer />}

      <Analytics />
      <SpeedInsights />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AdminProvider>
        <ProductProvider>
          <CartProvider>
            <AppLayout />
          </CartProvider>
        </ProductProvider>
      </AdminProvider>
    </BrowserRouter>
  );
}

export default App;
